import { useEffect, useState } from 'react';

type PreviewStyles = {
  previewTransformRotate: string;
  previewTransformOrigin: string;
  previewHeight: number;
  previewWidth: number;
  previewMarginTop: string | number;
  previewMarginRight: string | number;
  cardPreviewMarginTop: number;
  cardPreviewMarginLeft: number;
  foldPreviewMarginTop: number;
  foldPreviewMarginLeft: string | number;
  foldPreviewTransformRotate: string;
  foldPreviewTransformOrigin: string;
  foldPreviewWidth: number;
  foldPreviewHeight: number;
};

export function useGetPreviewStyling({
  pageHeight,
  pageWidth,
  isFoldable,
  pageOrientation,
  scale,
  hideFoldedDisplay,
  showRedesign = false,
}: {
  pageHeight: number;
  pageWidth: number;
  isFoldable: boolean;
  pageOrientation: unknown;
  scale: number;
  hideFoldedDisplay?: boolean;
  showRedesign?: boolean;
}): PreviewStyles {
  const [styles, setStyles] = useState<PreviewStyles>({
    previewTransformRotate: '',
    previewTransformOrigin: '',
    previewHeight: 0,
    previewWidth: 0,
    previewMarginTop: 0,
    previewMarginRight: 0,
    cardPreviewMarginTop: 0,
    cardPreviewMarginLeft: 0,
    foldPreviewMarginTop: 0,
    foldPreviewMarginLeft: 0,
    foldPreviewTransformRotate: '',
    foldPreviewTransformOrigin: '',
    foldPreviewWidth: 0,
    foldPreviewHeight: 0,
  });

  useEffect(() => {
    if (pageHeight && pageWidth && scale) {
      switch (pageOrientation) {
        case 'PORTRAIT':
          setStyles({
            previewTransformRotate:
              isFoldable && !hideFoldedDisplay
                ? `perspective(${pageHeight * scale * 0.4}px) rotateY(-1deg)`
                : '',
            previewTransformOrigin: 'top right',
            previewHeight: pageHeight * scale,
            previewWidth: pageWidth * scale, // Already cut in half (via getCardDimensionsInPoints) if foldable
            previewMarginTop: 0,
            previewMarginRight: isFoldable ? '6px' : 0,
            cardPreviewMarginTop: 0,
            cardPreviewMarginLeft: isFoldable ? -pageWidth : 0,
            foldPreviewMarginTop: 0,
            foldPreviewMarginLeft: 0,
            foldPreviewTransformRotate: `perspective(${pageHeight * scale * 0.6}px) rotateY(-1deg)`,
            foldPreviewTransformOrigin: 'bottom left',
            foldPreviewWidth: showRedesign ? pageWidth * scale * 0.92 : pageWidth * scale * 0.95,
            foldPreviewHeight: pageHeight * scale,
          });
          break;

        case 'LANDSCAPE':
        default:
          setStyles({
            previewTransformRotate:
              isFoldable && !hideFoldedDisplay
                ? `perspective(${pageWidth * scale * 0.2}px) rotateX(1deg)`
                : '',
            previewTransformOrigin: 'top center',
            previewHeight: pageHeight * scale, // Already cut in half (via getCardDimensionsInPoints) if foldable
            previewWidth: pageWidth * scale,
            previewMarginTop: isFoldable ? '-6px' : 0,
            previewMarginRight: 0,
            cardPreviewMarginTop: isFoldable ? -pageHeight : 0,
            cardPreviewMarginLeft: 0,
            foldPreviewMarginTop: showRedesign ? 0 : pageHeight * scale * 0.1,
            foldPreviewMarginLeft: 0,
            foldPreviewTransformRotate: showRedesign
              ? `perspective(${pageWidth * scale * 0.4}px) rotateX(1deg)`
              : `perspective(${pageWidth * scale * 0.2}px) rotateX(-1deg)`,
            foldPreviewTransformOrigin: showRedesign ? 'top left' : 'top center',
            foldPreviewWidth: pageWidth * scale,
            foldPreviewHeight: showRedesign ? pageHeight * scale * 0.92 : pageHeight * scale,
          });
          break;
      }
    }
  }, [hideFoldedDisplay, isFoldable, pageHeight, pageOrientation, pageWidth, scale, showRedesign]);

  return styles;
}
